import type { ReactElement } from "react";
import type { Category } from "../api/types";

/** What a garment drawing shows: its shape, rise, length and any alterations. */
export type GarmentLook = {
  cat: Category;
  kind?: string | null;
  shape?: string | null;
  rise?: string | null;
  petite?: boolean;
  length?: string;
  curvy?: boolean;
  alts?: string[];
};

// Proportions are exaggerated on purpose so the options read apart at a glance.
const PANT_SHAPE: Record<string, [knee: number, hem: number, roomy?: number]> = {
  straight: [42, 42], slim: [32, 26], skinny: [24, 14], wide_leg: [60, 74], flare: [26, 72], relaxed: [56, 54, 8], barrel: [72, 24],
};
const RISE_Y: Record<string, number> = { high: 12, mid: 26, low: 40 };
const TOP_SHAPE: Record<string, [chest: number, hem: number, hemY: number]> = {
  fitted: [40, 38, 210], relaxed: [54, 56, 214], oversized: [68, 70, 224], cropped: [52, 52, 140],
};
const DRESS_SHAPE: Record<string, [waist: number, hem: number]> = { shift: [38, 46], a_line: [24, 80], wrap: [24, 64], bodycon: [21, 30] };
const SKIRT_SHAPE: Record<string, number> = { a_line: 70, pencil: 36, full: 96 };

/** The plainest shape in each category, drawn faintly behind the others to compare against. */
export const BASE_SHAPE: Record<Category, string> = { pants: "straight", top: "relaxed", dress: "shift", skirt: "a_line" };

const r1 = (n: number) => Math.round(n * 10) / 10;

/** Tailor's arrows at both edges of a line: in (-1) or out (1). */
function arrows(xl: number, xr: number, y: number, dir: -1 | 1): string {
  const len = 9;
  const one = (x: number, side: -1 | 1) => {
    const tip = x + (dir < 0 ? -side * 2 : side * (len + 2));
    const tail = x + (dir < 0 ? side * (len + 2) : side * 2);
    const w = dir < 0 ? -side : side;
    return `M${r1(tail)} ${r1(y)} H${r1(tip)} M${r1(tip - w * 4)} ${r1(y - 3.5)} L${r1(tip)} ${r1(y)} L${r1(tip - w * 4)} ${r1(y + 3.5)}`;
  };
  return `${one(xl, -1)} ${one(xr, 1)}`;
}

type Paths = { body: string; stitch: string; ghost?: string; arrow?: string; rise?: [number, number, number] };

function paths(g: GarmentLook): Paths {
  const alts = g.alts ?? [];
  const has = (a: string) => alts.includes(a);
  const lenShift = g.petite ? -18 : g.length === "Short" ? -12 : g.length === "Tall" ? 10 : 0;
  const curvy = g.curvy ? 8 : 0;
  const hemmed = has("Hemmed");

  if (g.cat === "pants") {
    const [k0, h0, roomy = 0] = PANT_SHAPE[g.shape ?? ""] ?? PANT_SHAPE.straight;
    const taper = has("Tapered the leg") ? 8 : 0;
    const shorts = g.kind === "shorts";
    const wy = RISE_Y[g.rise ?? ""] ?? RISE_Y.mid;
    const waist = 44 + (has("Let out") ? 4 : 0) - (has("Took in the waist") ? 5 : 0);
    const knee = shorts ? 52 : k0 - taper / 2;
    const hem = shorts ? 54 : h0 - taper;
    const hemY = (shorts ? 132 : 226 + lenShift) - (hemmed ? 12 : 0);
    const hip = 52 + curvy + roomy;
    const cy = 104;
    const ky = Math.min(166, hemY - 20);
    const leg = (cx: number, side: -1 | 1) => ({ o: cx + side * (hem / 2), i: cx - side * (hem / 2), ko: cx + side * (knee / 2), ki: cx - side * (knee / 2) });
    const L = leg(61, -1);
    const R = leg(139, 1);
    const body =
      `M${r1(100 - waist)} ${wy} H${r1(100 + waist)} L${r1(100 + hip)} 64 Q${r1(R.ko + 2)} ${r1(ky)} ${r1(R.o)} ${r1(hemY)} H${r1(R.i)} ` +
      `Q${r1(R.ki)} ${r1(ky)} 100 ${cy} Q${r1(L.ki)} ${r1(ky)} ${r1(L.i)} ${r1(hemY)} H${r1(L.o)} Q${r1(L.ko - 2)} ${r1(ky)} ${r1(100 - hip)} 64 Z`;
    const stitch =
      `M${r1(100 - waist)} ${wy + 12} H${r1(100 + waist)} M100 ${wy + 12} V${cy - 22} ` +
      `M${r1(L.o + 3)} ${r1(hemY - 8)} H${r1(L.i - 3)} M${r1(R.i + 3)} ${r1(hemY - 8)} H${r1(R.o - 3)}`;
    const ghost = hemmed ? `M${r1(L.o)} ${r1(hemY + 12)} H${r1(L.i)} M${r1(R.i)} ${r1(hemY + 12)} H${r1(R.o)}` : undefined;
    const arrow = [
      has("Took in the waist") ? arrows(100 - waist, 100 + waist, wy + 6, -1) : "",
      has("Let out") ? arrows(100 - waist, 100 + waist, wy + 6, 1) : "",
      taper ? arrows(L.ko - 2, R.ko + 2, ky, -1) : "",
    ].join(" ").trim();
    return { body, stitch, ghost, arrow: arrow || undefined, rise: [100 + waist + 12, wy, cy] };
  }

  if (g.cat === "top") {
    const [ch0, hm0, hy0] = TOP_SHAPE[g.shape ?? ""] ?? TOP_SHAPE.relaxed;
    const sides = has("Took in the sides") ? 6 : 0;
    const chest = ch0 - sides + curvy / 2;
    const hem = hm0 - sides;
    const hy = hy0 + lenShift * 0.6 - (hemmed ? 12 : 0);
    const sl = has("Shortened sleeves") ? 0 : 1;
    const sh = 58;
    const body =
      `M76 26 Q100 42 124 26 L${100 + sh} 40 L${100 + sh + 8 + 16 * sl} ${70 + 30 * sl} L${100 + sh - 6 + 10 * sl} ${80 + 30 * sl} ` +
      `L${100 + sh - 10} ${64 + 10 * sl} L${r1(100 + chest)} 110 L${r1(100 + hem)} ${r1(hy)} H${r1(100 - hem)} L${r1(100 - chest)} 110 ` +
      `L${100 - sh + 10} ${64 + 10 * sl} L${100 - sh + 6 - 10 * sl} ${80 + 30 * sl} L${100 - sh - 8 - 16 * sl} ${70 + 30 * sl} L${100 - sh} 40 Z`;
    return {
      body,
      stitch: `M${r1(100 - hem + 4)} ${r1(hy - 9)} H${r1(100 + hem - 4)}`,
      ghost: hemmed ? `M${r1(100 - hem)} ${r1(hy + 12)} H${r1(100 + hem)}` : undefined,
      arrow: sides ? arrows(100 - chest, 100 + chest, 112, -1) : undefined,
    };
  }

  if (g.cat === "dress") {
    const [w0, hm0] = DRESS_SHAPE[g.shape ?? ""] ?? DRESS_SHAPE.a_line;
    const tookIn = has("Took it in") ? 4 : 0;
    const waist = w0 - tookIn + curvy / 2;
    const hem = hm0 + curvy;
    const hemY = 226 + lenShift - (hemmed ? 14 : 0);
    const wrap = g.shape === "wrap";
    const strapY = has("Shortened straps") ? 26 : 18;
    const body =
      `M80 ${strapY} L86 ${strapY} Q100 ${wrap ? 58 : 44} 114 ${strapY} L120 ${strapY} L134 70 L${r1(100 + waist)} 100 ` +
      `L${r1(100 + hem)} ${r1(hemY)} H${r1(100 - hem)} L${r1(100 - waist)} 100 L66 70 Z`;
    return {
      body,
      stitch:
        `M${r1(100 - waist)} 100 H${r1(100 + waist)} M${r1(100 - hem + 6)} ${r1(hemY - 9)} H${r1(100 + hem - 6)}` +
        (wrap ? ` M${r1(100 + waist)} 100 L${r1(100 - hem * 0.4)} ${r1(hemY)}` : ""),
      ghost: hemmed ? `M${r1(100 - hem)} ${r1(hemY + 14)} H${r1(100 + hem)}` : undefined,
      arrow: tookIn ? arrows(100 - waist, 100 + waist, 100, -1) : undefined,
    };
  }

  const tookIn = has("Took in the waist") ? 5 : 0;
  const waist = 38 - tookIn;
  const hem = (SKIRT_SHAPE[g.shape ?? ""] ?? SKIRT_SHAPE.a_line) + curvy;
  const hemY = 214 + lenShift - (hemmed ? 14 : 0);
  return {
    body: `M${100 - waist} 30 H${100 + waist} L${100 + waist + 3} 48 L${r1(100 + hem)} ${r1(hemY)} H${r1(100 - hem)} L${100 - waist - 3} 48 Z`,
    stitch: `M${100 - waist} 44 H${100 + waist} M${r1(100 - hem + 6)} ${r1(hemY - 9)} H${r1(100 + hem - 6)}`,
    ghost: hemmed ? `M${r1(100 - hem)} ${r1(hemY + 14)} H${r1(100 + hem)}` : undefined,
    arrow: tookIn ? arrows(100 - waist, 100 + waist, 38, -1) : undefined,
  };
}

/** A small flat drawing of a garment, for picking a shape, rise or alteration by eye. */
export function GarmentSketch(props: { look: GarmentLook; compare?: GarmentLook | null; showRise?: boolean; viewBox?: string }) {
  const { look, compare, showRise, viewBox = "0 0 200 240" } = props;
  const p = paths(look);
  const ref = compare ? paths(compare) : null;
  const out: ReactElement[] = [<path key="body" className="body" d={p.body} />, <path key="stitch" className="stitch" d={p.stitch} />];
  if (showRise && p.rise) {
    const [x, top, bottom] = p.rise;
    out.push(
      <path key="tape" className="tapeline" d={`M${r1(x)} ${top} V${bottom}`} />,
      <path key="ticks" className="tapetick" d={`M${r1(x - 4)} ${top} h8 M${r1(x - 4)} ${bottom} h8`} />,
    );
  }
  if (p.ghost) out.push(<path key="ghost" className="ghost" d={p.ghost} />);
  if (p.arrow) out.push(<path key="arrow" className="arrow" d={p.arrow} />);
  if (ref) out.push(<path key="ref" className="ref" d={ref.body} />);
  return (
    <svg className="garment" viewBox={viewBox} aria-hidden="true">
      {out}
    </svg>
  );
}
